import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { get_vendor_detail } from "../../utils/api";
import {
  Avatar,
  Badge,
  Box,
  Card,
  Divider,
  Flex,
  Heading,
  Icon,
  Input,
  SimpleGrid,
  Skeleton,
  SkeletonCircle,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import CustomButton from "../../components/ButtonComp";
import { BiArrowBack } from "react-icons/bi";
import ImageModal from "../../components/ImageModal";
import {
  FiBriefcase,
  FiUser,
  FiMapPin,
  FiPhone,
  FiMail,
  FiFileText,
  FiCreditCard,
  FiSettings,
  FiCheckCircle,
  FiXCircle,
} from "react-icons/fi";

// ── Section Header ─────────────────────────────────────────────────────────────
const SectionHeader = ({ icon, title, color = "blue.500", count }) => (
  <Flex align="center" gap={3} mb={4}>
    <Box
      w="34px"
      h="34px"
      borderRadius="9px"
      bg={`${color.split(".")[0]}.50`}
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      <Icon as={icon} color={color} boxSize={4} />
    </Box>
    <Flex align="center" gap={2}>
      <Text fontWeight="700" fontSize="md" color="gray.700">
        {title}
      </Text>
      {count != null && (
        <Badge colorScheme="blue" borderRadius="8px" px={2} fontSize="xs">
          {count}
        </Badge>
      )}
    </Flex>
  </Flex>
);

// ── Info Field ─────────────────────────────────────────────────────────────────
const InfoField = ({ label, value }) => (
  <Box>
    <Text
      fontSize="10px"
      fontWeight="700"
      color="gray.400"
      textTransform="uppercase"
      letterSpacing="0.6px"
      mb="3px"
    >
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="500" color="gray.800" wordBreak="break-word">
      {value || "N/A"}
    </Text>
  </Box>
);

// ── Yes/No Field ───────────────────────────────────────────────────────────────
const YesNoField = ({ label, value }) => (
  <Box>
    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.6px" mb="3px">
      {label}
    </Text>
    <Flex align="center" gap={1}>
      <Icon
        as={value ? FiCheckCircle : FiXCircle}
        color={value ? "green.500" : "red.400"}
        boxSize={4}
      />
      <Text fontSize="sm" fontWeight="600" color={value ? "green.600" : "red.500"}>
        {value ? "Yes" : "No"}
      </Text>
    </Flex>
  </Box>
);

// ── Editable Field ─────────────────────────────────────────────────────────────
const EditableField = ({ label, value, onChange, placeholder }) => (
  <Box>
    <Text
      fontSize="10px"
      fontWeight="700"
      color="gray.400"
      textTransform="uppercase"
      letterSpacing="0.6px"
      mb="5px"
    >
      {label}
    </Text>
    <Input
      type="number"
      placeholder={placeholder}
      value={value || ""}
      onChange={onChange}
      size="sm"
      borderRadius="8px"
      border="1.5px solid"
      borderColor="gray.200"
      bg="white"
      fontWeight="500"
      _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 2px #0175c220" }}
    />
  </Box>
);

// ── Section Card ───────────────────────────────────────────────────────────────
const SectionCard = ({ children }) => (
  <Card
    bg="white"
    borderRadius="14px"
    boxShadow="0 2px 8px rgba(0,0,0,0.06)"
    border="1.5px solid"
    borderColor="gray.100"
    p={6}
  >
    {children}
  </Card>
);

// ── Empty State ────────────────────────────────────────────────────────────────
const EmptyState = ({ message }) => (
  <Box
    bg="gray.50"
    borderRadius="10px"
    p={6}
    textAlign="center"
    border="1.5px dashed"
    borderColor="gray.200"
  >
    <Text fontSize="sm" color="gray.400">
      {message}
    </Text>
  </Box>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const VendorDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const { onOpen, onClose, isOpen } = useDisclosure();
  const [url, setUrl] = useState("");
  const [settingData, setSettingData] = useState({});

  useEffect(() => {
    get_vendor_detail(location.state, setLoading).then((el) => {
      if (el.success) {
        setData(el.reponselist);
        setSettingData({
          credit_days: el.reponselist.credit_days,
          credit_limit: el.reponselist.credit_limit,
        });
      }
    });
  }, []);

  // Payment settlement label
  const getPaymentSettlement = (credit) => {
    switch (String(credit)) {
      case "1": return "T + 2";
      case "2": return "T + 7";
      case "3": return "T + 15";
      default: return "T + 30";
    }
  };

  return (
    <>
      {/* ── Top Bar ────────────────────────────────────────────────────────── */}
      <Card
        display="flex"
        flexDirection="row"
        alignItems="center"
        justify="space-between"
        paddingInline={5}
        paddingBlock={3}
        boxShadow="sm"
        borderRadius="12px"
        mb={1}
      >
        <Flex align="center" gap={3}>
          <Box
            w="36px"
            h="36px"
            borderRadius="9px"
            bg="blue.50"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FiBriefcase} color="blue.500" boxSize={4} />
          </Box>
          <Box>
            <Heading fontSize="lg" color="gray.800">
              Vendor Details
            </Heading>
            {data?.tradename && (
              <Text fontSize="xs" color="gray.400">
                {data.tradename}
              </Text>
            )}
          </Box>
        </Flex>
        <CustomButton
          icon={<BiArrowBack color="#fff" />}
          title="Back"
          height="34px"
          b_color="#374151"
          onClick={() => navigate(-1)}
        />
      </Card>

      {/* ── Loading ───────────────────────────────────────────────────────── */}
      {loading ? (
        <VStack spacing={4} align="stretch" mt={4}>
          <SectionCard>
            <Flex gap={4} align="center" mb={6}>
              <SkeletonCircle size="16" />
              <Box flex={1}>
                <Skeleton h="18px" mb={2} w="45%" borderRadius="8px" />
                <Skeleton h="13px" w="30%" borderRadius="8px" />
              </Box>
            </Flex>
            <Skeleton h="12px" w="28%" mb={5} borderRadius="8px" />
            <SimpleGrid columns={[1, 2, 3]} spacing={5}>
              {[...Array(6)].map((_, i) => (
                <Box key={i}>
                  <Skeleton h="10px" mb={2} w="40%" borderRadius="6px" />
                  <Skeleton h="14px" w="70%" borderRadius="6px" />
                </Box>
              ))}
            </SimpleGrid>
          </SectionCard>
          <SectionCard>
            <Skeleton h="14px" w="25%" mb={4} borderRadius="8px" />
            <Skeleton h="80px" borderRadius="10px" />
          </SectionCard>
        </VStack>
      ) : (
        <VStack spacing={4} align="stretch" mt={4}>

          {/* ── Profile Banner ──────────────────────────────────────────────── */}
          <SectionCard>
            <Flex align="center" gap={5} flexWrap="wrap" mb={6}>
              <Avatar
                name={data?.tradename}
                bg="#0175c2"
                color="white"
                size="lg"
                fontWeight="bold"
              />
              <Box flex={1} minW="160px">
                <Text fontWeight="800" fontSize="xl" color="gray.800" mb={1}>
                  {data?.tradename || "N/A"}
                </Text>
                <Text fontSize="sm" color="gray.500" mb={2}>
                  {data?.legalname || ""}
                </Text>
                <Flex gap={2} flexWrap="wrap">
                  {data?.gst && (
                    <Badge colorScheme="teal" borderRadius="8px" px={3} py={1} fontSize="xs" fontWeight="600">
                      GST: {data.gst}
                    </Badge>
                  )}
                  {data?.pan && (
                    <Badge colorScheme="orange" borderRadius="8px" px={3} py={1} fontSize="xs" fontWeight="600">
                      PAN: {data.pan}
                    </Badge>
                  )}
                </Flex>
              </Box>
            </Flex>

            {/* General Info */}
            <SectionHeader icon={FiBriefcase} title="Business Registration" color="blue.500" />
            <SimpleGrid columns={[1, 2, 3]} spacing={5} mb={5}>
              <InfoField label="Trade Name" value={data?.tradename} />
              <InfoField label="Legal Name" value={data?.legalname} />
              <InfoField label="GST Number" value={data?.gst} />
              <InfoField label="PAN Number" value={data?.pan} />
            </SimpleGrid>

            <Divider mb={5} />

            {/* Address */}
            <SectionHeader icon={FiMapPin} title="Address Information" color="orange.500" />
            <SimpleGrid columns={[1, 2, 3]} spacing={5}>
              <InfoField label="Address" value={data?.address} />
              <InfoField label="District" value={data?.district} />
              <InfoField label="State" value={data?.state} />
              <InfoField label="Pincode" value={data?.pincode} />
            </SimpleGrid>
          </SectionCard>

          {/* ── Business & Personal Contact ─────────────────────────────────── */}
          <SimpleGrid columns={[1, 1, 2]} spacing={4}>

            {/* Business Detail */}
            <SectionCard>
              <SectionHeader icon={FiBriefcase} title="Business Contact" color="green.500" />
              <Flex direction="column" gap={4}>
                <Flex align="center" gap={2}>
                  <Icon as={FiPhone} color="green.400" boxSize={4} flexShrink={0} />
                  <Box>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb="2px">
                      Business Mobile
                    </Text>
                    <Text fontSize="sm" fontWeight="500" color="gray.800">
                      {data?.mobile_business || "N/A"}
                    </Text>
                  </Box>
                </Flex>
                <Flex align="center" gap={2}>
                  <Icon as={FiMail} color="green.400" boxSize={4} flexShrink={0} />
                  <Box>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb="2px">
                      Business Email
                    </Text>
                    <Text fontSize="sm" fontWeight="500" color="gray.800">
                      {data?.email_business || "N/A"}
                    </Text>
                  </Box>
                </Flex>
                <Flex align="center" gap={2}>
                  <Icon as={FiMapPin} color="green.400" boxSize={4} flexShrink={0} />
                  <Box>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb="2px">
                      Business Address
                    </Text>
                    <Text fontSize="sm" fontWeight="500" color="gray.800">
                      {data?.address_business || "N/A"}
                    </Text>
                  </Box>
                </Flex>
              </Flex>
            </SectionCard>

            {/* Personal Detail */}
            <SectionCard>
              <SectionHeader icon={FiUser} title="Personal Contact" color="purple.500" />
              <Flex direction="column" gap={4}>
                <Flex align="center" gap={2}>
                  <Icon as={FiPhone} color="purple.400" boxSize={4} flexShrink={0} />
                  <Box>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb="2px">
                      Personal Mobile
                    </Text>
                    <Text fontSize="sm" fontWeight="500" color="gray.800">
                      {data?.mobile_personal || "N/A"}
                    </Text>
                  </Box>
                </Flex>
                <Flex align="center" gap={2}>
                  <Icon as={FiMail} color="purple.400" boxSize={4} flexShrink={0} />
                  <Box>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb="2px">
                      Personal Email
                    </Text>
                    <Text fontSize="sm" fontWeight="500" color="gray.800">
                      {data?.email_personal || "N/A"}
                    </Text>
                  </Box>
                </Flex>
                <Flex align="center" gap={2}>
                  <Icon as={FiMapPin} color="purple.400" boxSize={4} flexShrink={0} />
                  <Box>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb="2px">
                      Personal Address
                    </Text>
                    <Text fontSize="sm" fontWeight="500" color="gray.800">
                      {data?.address_personal || "N/A"}
                    </Text>
                  </Box>
                </Flex>
              </Flex>
            </SectionCard>

          </SimpleGrid>

          {/* ── Settings ────────────────────────────────────────────────────── */}
          <SectionCard>
            <SectionHeader icon={FiSettings} title="Settings & Credit Configuration" color="gray.500" />
            <SimpleGrid columns={[1, 2, 3]} spacing={5}>
              <YesNoField
                label="Turnover > ₹10Cr (Previous 3 Years)"
                value={data?.turnover_p3 == 2}
              />
              <InfoField
                label="Payment Settlement"
                value={getPaymentSettlement(data?.credit)}
              />
              <YesNoField
                label="Bank Guarantee Request"
                value={data?.bank_guarantee_req == 2}
              />
              <YesNoField
                label="Credit Support from FI"
                value={data?.credit_fi_req == 2}
              />
              <EditableField
                label="Credit Days"
                placeholder="Enter credit days"
                value={settingData.credit_days}
                onChange={(e) =>
                  e.target.value >= 0 &&
                  setSettingData({ ...settingData, credit_days: e.target.value })
                }
              />
              <EditableField
                label="Credit Limit"
                placeholder="Enter credit limit"
                value={settingData.credit_limit}
                onChange={(e) =>
                  e.target.value >= 0 &&
                  setSettingData({ ...settingData, credit_limit: e.target.value })
                }
              />
            </SimpleGrid>
          </SectionCard>

          {/* ── Documents ────────────────────────────────────────────────────── */}
          <SectionCard>
            <SectionHeader
              icon={FiFileText}
              title="Document Details"
              color="purple.500"
              count={data?.documents?.length}
            />
            {!data?.documents || data.documents.length === 0 ? (
              <EmptyState message="No documents available" />
            ) : (
              <SimpleGrid columns={[1, 2]} spacing={3}>
                {data.documents.map((el, i) => (
                  <Box
                    key={i}
                    bg="gray.50"
                    borderRadius="10px"
                    border="1.5px solid"
                    borderColor="gray.100"
                    p={4}
                  >
                    <Flex justify="space-between" align="flex-start" mb={3}>
                      <Box flex={1} mr={3}>
                        <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb="2px">
                          Document Name
                        </Text>
                        <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>
                          {el.name || "N/A"}
                        </Text>
                      </Box>
                      <CustomButton
                        title="View"
                        height="28px"
                        onClick={() => { setUrl(el.url); onOpen(); }}
                      />
                    </Flex>
                    <Box>
                      <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb="2px">
                        Doc Type
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {el.doc_name || "N/A"}
                      </Text>
                    </Box>
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </SectionCard>

          {/* ── Bank Details ─────────────────────────────────────────────────── */}
          <SectionCard>
            <SectionHeader
              icon={FiCreditCard}
              title="Bank Details"
              color="teal.500"
              count={data?.banklist?.length}
            />
            {!data?.banklist || data.banklist.length === 0 ? (
              <EmptyState message="No bank accounts available" />
            ) : (
              <SimpleGrid columns={[1, 2]} spacing={3}>
                {data.banklist.map((el, i) => (
                  <Box
                    key={i}
                    bg="gray.50"
                    borderRadius="10px"
                    border="1.5px solid"
                    borderColor="gray.100"
                    p={4}
                  >
                    <Flex justify="space-between" align="flex-start" mb={3}>
                      <Box flex={1} mr={3}>
                        <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb="2px">
                          Account Holder
                        </Text>
                        <Text fontSize="sm" fontWeight="600" color="gray.800">
                          {el.name || "N/A"}
                        </Text>
                      </Box>
                      {el.url && (
                        <CustomButton
                          title="View"
                          height="28px"
                          onClick={() => { setUrl(el.url); onOpen(); }}
                        />
                      )}
                    </Flex>
                    <SimpleGrid columns={2} spacing={3}>
                      <InfoField label="Bank Name" value={el.bankname} />
                      <InfoField label="Account No." value={el.account_no} />
                      <InfoField label="Branch" value={el.branch_name} />
                      <InfoField label="IFSC Code" value={el.ifsc_code} />
                    </SimpleGrid>
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </SectionCard>

        </VStack>
      )}

      <ImageModal url={url} onClose={onClose} isOpen={isOpen} />
    </>
  );
};

export default VendorDetail;
